import React, { useCallback, useState } from "react";

import { Text, Center, Button, useToast, Spinner } from "native-base";
import { useMutation } from "react-relay";

import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import { ArchivedIcon, TickIcon, TrashIcon } from "pianofunclub-shared/components/Other/Icons";
import type {
  DeleteEnrolmentMutation,
  DeleteEnrolmentMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/DeleteEnrolmentMutation.graphql";
import { delete_enrolment } from "pianofunclub-shared/relay/graphql/accounts/DeleteEnrolment";

export interface EnrolmentStatusActionsheetProps {
  editEnrolment: (
    variables: {
      adminNotes?: string;
      instrument?: string;
      lessonDuration?: number;
      lessonStage?: string;
      lessonType?: string;
      parentEmail?: string;
      parentName?: string;
      parentPhoneNumber?: string;
      positionInWaitingList?: number;
      pupilFirstName?: string;
      pupilLastName?: string;
      pupilNotes?: string;
      schoolClass?: string;
      schoolId?: string;
      schoolYear?: number;
      status?: string;
    },
    options?: {
      isUndo?: boolean;
    }
  ) => void;
  enrolmentId: string | undefined;
  enrolmentSchoolIsArchived: boolean;
  enrolmentsConnectionId: string | undefined;
  enrolmentStatus: string | null | undefined;
  enrolPupil: (variables: { onComplete?: () => void }) => void;
  enrolPupilInFlight: boolean;
  isOpen: boolean;
  onClose: () => void;
  onDeleteEnrolment?: () => void;
}

const EnrolmentStatusActionsheet = (props: EnrolmentStatusActionsheetProps) => {
  const {
    editEnrolment,
    enrolmentId,
    enrolmentSchoolIsArchived,
    enrolmentsConnectionId,
    enrolmentStatus,
    enrolPupil,
    enrolPupilInFlight,
    isOpen,
    onClose,
    onDeleteEnrolment,
  } = props;

  const [commitDeleteEnrolment, deleteEnrolmentInFlight] =
    useMutation<DeleteEnrolmentMutation>(delete_enrolment);

  const toast = useToast();

  const [deleteEnrolmentAlertIsOpen, setDeleteEnrolmentAlertIsOpen] = useState(false);

  const deleteEnrolment = useCallback(() => {
    if (!enrolmentId) {
      return;
    }

    const deleteEnrolmentConfig = {
      variables: {
        connections: enrolmentsConnectionId ? [enrolmentsConnectionId] : [],
        input: {
          enrolmentId: enrolmentId,
        },
      },
      onCompleted: (response: DeleteEnrolmentMutation$data) => {
        onClose();
        setDeleteEnrolmentAlertIsOpen(false);
        if (response?.deleteEnrolment?.success) {
          onDeleteEnrolment?.();
          toast.show({
            render: ({ id }: { id: string }) => (
              <ToastAlert id={id} status="error" title={"Enrolment deleted"} toast={toast} />
            ),
          });
        } else {
          toast.show({
            render: ({ id }: { id: string }) => (
              <ToastAlert
                description={"Please get in touch with one of the team"}
                id={id}
                status="error"
                title={"Couldn't delete enrolment"}
                toast={toast}
              />
            ),
          });
        }
      },
    };

    commitDeleteEnrolment(deleteEnrolmentConfig);
  }, [
    commitDeleteEnrolment,
    enrolmentId,
    enrolmentsConnectionId,
    onClose,
    onDeleteEnrolment,
    toast,
  ]);

  return (
    <>
      <Actionsheet
        animationType="fade"
        hideDragIndicator
        isOpen={isOpen}
        justifyContent="center"
        onClose={onClose}
        size="lg"
      >
        <Actionsheet.Content
          alignItems="center"
          closeButtonProps={{ right: "5", top: "5" }}
          justifyContent="center"
          mx="auto"
          roundedBottom={12}
          roundedTop={12}
          showCloseButton
          w="40%"
        >
          <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
            {"Update Enrolment"}
          </Text>
          {(enrolmentStatus === "OFFERED" || enrolmentStatus === "OFFER_EXPIRED") && (
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  {!enrolPupilInFlight ? (
                    <TickIcon color="primary.600" size="7" />
                  ) : (
                    <Spinner color="primary.400" />
                  )}
                </Center>
              }
              onPress={() => {
                enrolPupil({
                  onComplete: onClose,
                });
              }}
            >
              {"Enrol pupil"}
            </Actionsheet.Item>
          )}
          {enrolmentStatus !== "ARCHIVED" && enrolmentStatus !== "ENROLED" ? (
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  <ArchivedIcon color="surface.600" size="6" />
                </Center>
              }
              onPress={() => {
                editEnrolment({
                  status: "ARCHIVED",
                });
                onClose();
              }}
            >
              {"Archive enrolment"}
            </Actionsheet.Item>
          ) : (
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              isDisabled={enrolmentSchoolIsArchived}
              leftIcon={
                <Center size="7">
                  <ArchivedIcon color="primary.500" size="6" />
                </Center>
              }
              onPress={() => {
                editEnrolment({
                  status: "WAITING_LIST",
                });
                onClose();
              }}
            >
              {"Unarchive enrolment"}
            </Actionsheet.Item>
          )}
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <TrashIcon color="surface.800" size="6" />
              </Center>
            }
            onPress={() => {
              setDeleteEnrolmentAlertIsOpen(true);
            }}
          >
            {"Delete enrolment"}
          </Actionsheet.Item>
        </Actionsheet.Content>
      </Actionsheet>
      <AlertPopup
        alertIsOpen={deleteEnrolmentAlertIsOpen}
        body="Unless it was added by mistake or is a duplicate, you should archive it instead."
        header={"Are you sure you want to permanently delete this enrolment from the waiting list?"}
        setAlertIsOpen={(isOpen: boolean) => setDeleteEnrolmentAlertIsOpen(isOpen)}
      >
        <Button
          _text={{ fontSize: "lg" }}
          colorScheme="muted"
          height="50px"
          minWidth="100"
          onPress={() => {
            setDeleteEnrolmentAlertIsOpen(false);
          }}
          width="40%"
        >
          No, go back
        </Button>
        <ButtonDebounced
          _text={{ fontSize: "lg" }}
          colorScheme="red"
          height="50px"
          isLoading={deleteEnrolmentInFlight}
          minWidth="100"
          onPress={deleteEnrolment}
          width="40%"
        >
          Yes, delete
        </ButtonDebounced>
      </AlertPopup>
    </>
  );
};

export default React.memo(EnrolmentStatusActionsheet);
